import React from "react";

/**
 * MatrixField — rows × columns grid where each cell is an input of `cell_type`.
 *
 * Storage shape:
 *   { "<row label>": { "<column label>": value, ... }, ... }
 *
 * When `cell_type === "single_select"` each row behaves as a radio group across
 * its columns (only one column can be selected per row). Other cell types get
 * a normal input per cell.
 */
function coerceValue(value, rows, columns) {
  const out = {};
  for (const r of rows) {
    const rowVal = (value && value[r]) || {};
    out[r] = {};
    for (const c of columns) out[r][c] = rowVal[c] ?? "";
  }
  return out;
}

function CellInput({ cellType, value, onChange, ariaLabel }) {
  const common = {
    value: value ?? "",
    onChange: (e) => onChange(e.target.value),
    "aria-label": ariaLabel,
    className:
      "w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm outline-none focus:border-[color:var(--brand-accent,#E91E63)]/70 focus:ring-1 focus:ring-[color:var(--brand-accent,#E91E63)]/40",
  };
  if (cellType === "number") return <input type="number" step="any" {...common} />;
  if (cellType === "date") return <input type="date" {...common} />;
  if (cellType === "long_text")
    return <textarea rows={2} {...common} className={`${common.className} resize-y`} />;
  return <input type="text" {...common} />;
}

export default function MatrixField({ field, value, onChange }) {
  const rows = field.matrix_rows || [];
  const columns = field.matrix_columns || [];
  const cellType = field.cell_type || "short_text";
  const data = coerceValue(value, rows, columns);
  const isRadio = cellType === "single_select";

  const setCell = (row, col, v) => {
    if (isRadio) {
      // Only one column can be selected per row — clear the others.
      const nextRow = Object.fromEntries(columns.map((c) => [c, c === col ? v : ""]));
      onChange({ ...data, [row]: nextRow });
    } else {
      onChange({
        ...data,
        [row]: { ...(data[row] || {}), [col]: v },
      });
    }
  };

  return (
    <div
      className="rounded-lg border border-border bg-white overflow-x-auto"
      data-testid={`matrix-field-${field.field_id}`}
    >
      <table className="w-full text-sm min-w-[560px]">
        <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
          <tr>
            <th className="px-3 py-2 font-medium bg-transparent w-[38%]"></th>
            {columns.map((col) => (
              <th key={col} className="px-3 py-2 font-medium text-center">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => {
            const rowGroupName = `${field.field_id}--${row}`;
            const selectedForRow = isRadio
              ? columns.find((c) => (data[row]?.[c] ?? "") !== "")
              : null;
            return (
              <tr key={row} className="align-top">
                <td className="px-3 py-2.5 text-sm text-foreground/90 font-medium">{row}</td>
                {columns.map((col) => (
                  <td key={col} className="px-3 py-2 text-center">
                    {isRadio ? (
                      <label
                        className="inline-flex items-center justify-center cursor-pointer group"
                        data-testid={`matrix-${field.field_id}-${row}-${col}`}
                      >
                        <input
                          type="radio"
                          name={rowGroupName}
                          checked={selectedForRow === col}
                          onChange={() => setCell(row, col, col)}
                          className="peer sr-only"
                          aria-label={`${row}: ${col}`}
                        />
                        <span
                          className="w-4 h-4 rounded-full border border-foreground/30 group-hover:border-foreground/70 peer-checked:bg-[color:var(--brand-accent,#E91E63)] peer-checked:border-[color:var(--brand-accent,#E91E63)] transition"
                        />
                      </label>
                    ) : (
                      <CellInput
                        cellType={cellType}
                        value={data[row]?.[col]}
                        onChange={(v) => setCell(row, col, v)}
                        ariaLabel={`${row}: ${col}`}
                      />
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
